import { TradingFormState } from '../../trading-form/trading-form.types';
import { v4 as uuidv4 } from 'uuid';
import { DataFeedInput } from '@server/data-feed/schemas/input.schema';
import { IndicatorType } from '@shared/types';
import {
  IndicatorConfigInput,
  SignalCondition,
  StrategyConfigInput
} from '@server/data-feed/schemas/input.schema';

export const isValidState = ({
  marketConfig,
  timeframes
}: TradingFormState): boolean => {
  const { symbol, exchangeId, file } = marketConfig || {};

  const { timeframe } = timeframes?.[0] || {};

  return Boolean(
    symbol &&
      timeframe &&
      (exchangeId /*&&(historicalDays || historicalCandles))*/ || file)
  );
};

export const createDataFeedInput = (
  state: TradingFormState,
  feedId: string
): DataFeedInput => ({
  feedId,
  marketConfig: state.marketConfig!,
  timeframes: (state.timeframes || []).map((timeframe) => ({
    ...timeframe,
    indicators: stripUiOnlyFieldsFromIndicators(timeframe.indicators),
    strategy: stripUiOnlyFieldsFromStrategy(timeframe.strategy)
  })),
  positionManagementConfig: state.positionManagementConfig as any,
  tradingScheduleConfig: state.tradingScheduleConfig as any
});

const stripUiOnlyFieldsFromIndicators = (
  indicators: NonNullable<TradingFormState['timeframes']>[number]['indicators']
): IndicatorConfigInput[] => {
  // Indicator visualization is UI-only (local/session concerns),
  // so it must never be sent to server-side indicator handlers.
  return stripFieldsDeep(indicators, ['visualization']);
};

const stripUiOnlyFieldsFromStrategy = (
  strategy: StrategyConfigInput | undefined
): StrategyConfigInput | undefined => {
  const filteredStrategy = filterEnabledSignalConditions(strategy);
  // Strategy plot flags are UI-only and should not affect signal computation.
  // Disabled signal rows are kept in form state/templates, but excluded from feed input.
  return stripFieldsDeep(filteredStrategy, ['plot', 'visualization', 'enabled']);
};

const filterEnabledSignalConditions = (
  strategy: StrategyConfigInput | undefined
): StrategyConfigInput | undefined => {
  if (!strategy?.value) {
    return strategy;
  }

  type StrategySide = NonNullable<
    NonNullable<StrategyConfigInput['value']>['long']
  >;

  const filterConditions = (
    conditions: SignalCondition[] | undefined
  ): SignalCondition[] => {
    return (conditions ?? []).filter((condition) => condition.enabled !== false);
  };

  const filterSide = (side: StrategySide | undefined): StrategySide | undefined => {
    if (!side) {
      return undefined;
    }

    return {
      ...side,
      entries: filterConditions(side.entries),
      exits: filterConditions(side.exits)
    };
  };

  return {
    ...strategy,
    value: {
      ...strategy.value,
      long: filterSide(strategy.value.long),
      short: filterSide(strategy.value.short)
    }
  };
};

const stripFieldsDeep = <T>(
  value: T,
  fieldsToStrip: readonly string[]
): T => {
  if (value == null) {
    return value;
  }

  const stripSet = new Set(fieldsToStrip);

  const stripValue = (node: unknown): unknown => {
    if (node == null || typeof node !== 'object') {
      return node;
    }

    if (Array.isArray(node)) {
      return node.map(stripValue);
    }

    const result: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(
      node as Record<string, unknown>
    )) {
      if (stripSet.has(key)) {
        continue;
      }
      result[key] = stripValue(nestedValue);
    }

    return result;
  };

  return stripValue(value) as T;
};

type IndicatorSettings = {
  color?: string;
  plot?: boolean;
  subplot?: boolean;
};

const indicatorConfig: Record<IndicatorType, IndicatorSettings> = {
  RSI: {
    subplot: true,
    color: 'blue'
  },
  MA: {
    color: 'red'
  },
  VWAP: {},
  MACD: {
    // color: "red",
    subplot: true
  },
  ORDER_BLOCKS: {},
  ZIG_ZAG: {},
  MASTER_PATTERN: {},
  MARKET_STRUCTURE: {},
  LIQUIDATION: {}
};

export const getOverlayIndicators = (): IndicatorType[] =>
  Object.entries(indicatorConfig)
    .filter(([, config]) => !config.subplot)
    .map(([key]) => key as IndicatorType);

export const getSubplotIndicators = (): IndicatorType[] =>
  Object.entries(indicatorConfig)
    .filter(([, config]) => config.subplot)
    .map(([key]) => key as IndicatorType);

export const generateUniqueId = () => uuidv4();

export const getRandomHexColor = () => {
  // Generate random values for R, G, and B
  const r = Math.floor(Math.random() * 256); // 0-255
  const g = Math.floor(Math.random() * 256); // 0-255
  const b = Math.floor(Math.random() * 256); // 0-255

  // Convert to hexadecimal and pad with zeros if necessary
  const toHex = (value: number) => value.toString(16).padStart(2, '0');

  // Combine into a hex color string
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const maColorMapping: Record<string, string> = {
  7: 'pink',
  9: 'grey',
  20: 'orange',
  50: 'blue',
  100: 'brown',
  200: 'red'
};
